import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import {
  ArrowRight,
  Brain,
  Eye,
  Wifi,
  Cpu,
  Gauge,
  Battery,
  Shield,
  Zap,
  ChevronLeft,
  ChevronRight,
  Droplet,
  Construction,
  Layers,
  Factory,
} from 'lucide-react';
import { useState, useRef } from 'react';

/* ───────────── data ───────────── */

const stats = [
  { value: '99.9%', label: 'Accuracy' },
  { value: '5 km', label: 'Range' },
  { value: '24/7', label: 'Operation' },
  { value: '100%', label: 'Autonomous' },
];

const technologies = [
  {
    icon: Brain,
    title: 'AI-Powered Navigation',
    description:
      'Advanced machine learning algorithms for autonomous path planning and obstacle avoidance.',
  },
  {
    icon: Eye,
    title: 'Multi-Sensor Fusion',
    description:
      'HD cameras, 3D LIDAR, thermal imaging, and environmental sensors for complete situational awareness.',
  },
  {
    icon: Wifi,
    title: 'Fiber-Optic Tether',
    description:
      'High-speed data transmission enabling real-time video streaming and bi-directional control.',
  },
  {
    icon: Cpu,
    title: 'Edge Computing',
    description:
      'Onboard AI processing for instant data analysis and autonomous decision-making.',
  },
  {
    icon: Gauge,
    title: 'Environmental Monitoring',
    description:
      'Real-time tracking of temperature, humidity, air quality, and hazardous gas detection.',
  },
  {
    icon: Battery,
    title: 'Extended Operation',
    description:
      'Modular power system supporting multi-hour missions in extreme conditions.',
  },
];

const applications = [
  {
    icon: Droplet,
    title: 'Pipeline Inspection',
    description:
      'Comprehensive internal assessment of oil, gas, water, and sewage pipelines with millimeter precision.',
    features: ['Corrosion detection', 'Leak identification', 'Structural integrity analysis'],
    image: 'https://images.pexels.com/photos/35390345/pexels-photo-35390345.jpeg',
  },
  {
    icon: Construction,
    title: 'HDD Support',
    description:
      'Horizontal Directional Drilling support for underground infrastructure installation and verification.',
    features: ['Path verification', 'Obstacle detection', 'Quality assurance'],
    image: 'https://images.pexels.com/photos/16544054/pexels-photo-16544054.jpeg',
  },
  {
    icon: Layers,
    title: 'Tunnel Surveying',
    description:
      'Industrial tunnel and underground infrastructure mapping with 3D precision and environmental monitoring.',
    features: ['3D mapping', 'Environmental monitoring', 'Safety assessment'],
    image: 'https://images.pexels.com/photos/3906573/pexels-photo-3906573.jpeg',
  },
  {
    icon: Factory,
    title: 'Industrial Maintenance',
    description:
      'Predictive maintenance solutions for critical infrastructure in manufacturing and processing facilities.',
    features: ['Defect detection', 'Wear analysis', 'Preventive insights'],
    image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg',
  },
];

const specs = [
  { label: 'Max Range', value: '5 km' },
  { label: 'Autonomy', value: '100% Autonomous' },
  { label: 'Sensors', value: 'HD Camera, 3D LiDAR, Thermal, Environmental' },
  { label: 'Communication', value: 'Fiber-Optic Tether' },
  { label: 'Processing', value: 'Onboard Edge AI' },
  { label: 'Operation', value: '24/7 Continuous' },
  { label: 'Accuracy', value: '99.9%' },
  { label: 'Power', value: 'Modular, Multi-Hour' },
];

/* ───────────── page ───────────── */

const Vulcan = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div className="min-h-screen bg-primary">
      <Header />
      <ScrollToTop />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ paddingTop: '80px' }}>
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxBSSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzY2NzkyODc3fDA&ixlib=rb-4.1.0&q=85"
            alt="Vulcan Series"
            className="w-full h-full object-cover opacity-30"
          />
          <div
            className="absolute inset-0"
            style={{ background: isLight ? 'linear-gradient(180deg, rgba(245,245,247,0.75) 0%, rgba(245,245,247,0.97) 100%)' : 'linear-gradient(180deg, rgba(11,14,19,0.7) 0%, rgba(11,14,19,0.95) 100%)' }}
          />
        </div>

        <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: '#F97316' }} />
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: '#A64425' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center space-y-8">
          <span className="text-sm font-bold tracking-widest" style={{ color: '#F97316' }}>
            VULCAN SERIES
          </span>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground-primary">
            Autonomous Pipe
            <br />
            <span style={{ color: '#F97316' }}>Inspection Robots</span>
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-foreground-secondary">
            Next-generation robotics for extreme industrial environments. The Vulcan Series Pipe Rover brings
            autonomous intelligence to pipeline inspection and industrial surveying.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="px-8 py-6 text-lg font-semibold transition-smooth group"
              style={{ background: '#F97316', color: '#FFFFFF' }}
              onClick={() => navigate('/contact')}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              Get a Quote
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 pt-12">
            {stats.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                {i > 0 && <span className={`hidden md:block w-px h-10 -ml-4 mr-1 ${isLight ? 'bg-black/10' : 'bg-white/10'}`} />}
                <span className="text-2xl md:text-3xl font-bold" style={{ color: '#F97316' }}>{s.value}</span>
                <span className="text-sm text-foreground-secondary">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#35474B' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Core Technologies</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Cutting-edge innovation powering the Vulcan Series Pipe Rover
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="glass-frosted rounded-3xl overflow-hidden glow-subtle">
              <img
                src="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxBSSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzY2NzkyODc3fDA&ixlib=rb-4.1.0&q=85"
                alt="Technology"
                className="w-full h-full object-cover min-h-[400px]"
              />
            </div>
            <div className="space-y-0">
              {technologies.map((tech, i) => (
                <div key={i} className="flex items-start gap-4 py-5 border-b last:border-b-0" className="border-subtle">
                  <tech.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                  <div>
                    <h3 className="text-base font-semibold text-foreground-primary mb-1">{tech.title}</h3>
                    <p className="text-sm leading-relaxed text-foreground-secondary">{tech.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <ApplicationsSlider applications={applications} />

      {/* Specifications */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Technical Specifications</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Built for the most demanding environments
            </p>
          </div>

          <div className="glass-frosted rounded-3xl p-8 md:p-12 glow-subtle overflow-x-auto">
            <table className="w-full">
              <tbody>
                {specs.map((spec, i) => (
                  <tr key={i} className="border-b last:border-b-0" className="border-subtle">
                    <td className="py-4 pr-8 text-sm text-foreground-secondary whitespace-nowrap">{spec.label}</td>
                    <td className="py-4 text-right text-foreground-primary font-semibold">{spec.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">
            Ready to Transform Your Inspection Process?
          </h2>
          <p className="text-lg text-foreground-secondary max-w-2xl mx-auto">
            Get in touch to learn how the Vulcan Series can enhance safety, accuracy, and efficiency for your operations.
          </p>
          <Button
            size="lg"
            className="px-8 py-6 text-lg font-semibold transition-smooth group"
            style={{ background: '#F97316', color: '#FFFFFF' }}
            onClick={() => navigate('/contact')}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Contact Us
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Vulcan;

/* ───────────── sub-components ───────────── */

/* TechCard removed — technologies now rendered inline */

const ApplicationsSlider = ({ applications }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => { touchEndX.current = null; touchStartX.current = e.targetTouches[0].clientX; };
  const onTouchMove = (e) => { touchEndX.current = e.targetTouches[0].clientX; };
  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const d = touchStartX.current - touchEndX.current;
    if (d > minSwipeDistance) nextSlide();
    if (d < -minSwipeDistance) prevSlide();
  };

  const prevSlide = () => setActiveIndex((i) => (i - 1 + applications.length) % applications.length);
  const nextSlide = () => setActiveIndex((i) => (i + 1) % applications.length);
  const active = applications[activeIndex];

  return (
    <section className="py-24 px-6 relative overflow-hidden">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-14 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Applications</h2>
          <p className="text-lg text-foreground-secondary">Real-world deployments across critical infrastructure</p>
        </div>

        <div className="glass-frosted rounded-3xl overflow-hidden glow-subtle">
          <div className="grid md:grid-cols-[2fr_1fr] md:items-stretch">
            <div
              className="relative h-[460px] md:h-[560px] touch-pan-y"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
              <button onClick={prevSlide} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur items-center justify-center hover:bg-black/60 transition">
                <ChevronLeft className="text-white" />
              </button>
              <button onClick={nextSlide} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur items-center justify-center hover:bg-black/60 transition">
                <ChevronRight className="text-white" />
              </button>
            </div>

            <div className="h-full p-6 md:p-10 flex flex-col md:justify-center border-t md:border-t-0 md:border-l border-black/5 dark:border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}
                >
                  <active.icon size={20} style={{ color: '#F97316' }} />
                </div>
                <h3 className="text-xl font-bold text-foreground-primary">{active.title}</h3>
              </div>

              <p className="text-sm text-foreground-secondary mb-4">{active.description}</p>

              <ul className="space-y-2 mb-6">
                {active.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    <span className="text-sm text-foreground-secondary">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex gap-2 mt-auto">
                {applications.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`h-2 rounded-full transition-all ${i === activeIndex ? 'w-6 bg-orange-500' : 'w-2 bg-black/20 dark:bg-white/30'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
